function addEventListener(type, handler) {
  document.addEventListener(type, handler);
  return {
    remove() { document.removeEventListener(type, handler); },
  };
}

function isOverContainer(container, x, y) {
  if (!container) return true;
  
  const elementAtPoint = document.elementFromPoint(x, y);
  return elementAtPoint && container.contains(elementAtPoint);
}

const clickTolerance = 5;

/**
 * Resolve the distance prop from either an Int or an Object
 * @return {Object}
 */
function normalizeDistance(_distance = 0) {
  let distance = _distance;
  if (typeof distance !== 'object') {
    distance = { top: distance, left: distance, right: distance, bottom: distance };
  }

  return distance;
}

function pageOffset(dir) {
  if (dir === 'left') {
    return window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft || 0;
  }

  if (dir === 'top') {
    return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
  }

  return null;
}

/**
 * Given a node, get everything needed to calculate its boundaries
 * @param  {HTMLElement} node
 * @return {Object}
 */
export function getBoundsForNode(node) {
  if (!node || !node.getBoundingClientRect) return node;

  const rect = node.getBoundingClientRect();
  const left = rect.left + pageOffset('left');
  const top = rect.top + pageOffset('top');

  return {
    top,
    left,
    right: rect.width + left,
    bottom: rect.height + top,
    width: rect.width,
    height: rect.height
  };
}

/**
 * Given two objects containing "top", "left", "offsetWidth" and "offsetHeight"
 * properties, determine if they collide.
 * @param  {Object|HTMLElement} a
 * @param  {Object|HTMLElement} b
 * @return {bool}
 */
export function objectsCollide(nodeA, nodeB, tolerance = 0) {
  const aBounds = getBoundsForNode(nodeA);
  const bBounds = getBoundsForNode(nodeB);
  
  const { top: aTop, left: aLeft, right: aRight = aLeft, bottom: aBottom = aTop } = aBounds;
  const { top: bTop, left: bLeft, right: bRight = bLeft, bottom: bBottom = bTop } = bBounds;

  return !(
    // 'a' bottom doesn't touch 'b' top
    ((aBottom - tolerance) < bTop) ||
    // 'a' top doesn't touch 'b' bottom
    ((aTop + tolerance) > (bBottom)) ||
    // 'a' right doesn't touch 'b' left
    ((aRight - tolerance) < bLeft) ||
    // 'a' left doesn't touch 'b' right
    ((aLeft + tolerance) > (bRight))
  );
}

class Selection {

  constructor(node, global = false) {
    this.container = node;
    this.globalMouse = !node || global;

    this._listeners = Object.create(null);

    this._mouseDown = this._mouseDown.bind(this);
    this._mouseUp = this._mouseUp.bind(this);
    this._openSelector = this._openSelector.bind(this);
    this._keyListener = this._keyListener.bind(this);

    this._onMouseDownListener = addEventListener('mousedown', this._mouseDown);
    this._onKeyDownListener = addEventListener('keydown', this._keyListener);
    this._onKeyUpListener = addEventListener('keyup', this._keyListener);
  }

  on(type, handler) {
    const handlers = this._listeners[type] || (this._listeners[type] = []);

    handlers.push(handler);

    return {
      remove() {
        const idx = handlers.indexOf(handler);
        if (idx !== -1) handlers.splice(idx, 1);
      },
    };
  }

  emit(type, ...args) {
    const handlers = this._listeners[type] || [];
    handlers.forEach(fn => fn(...args));
  }

  teardown() {
    this.listeners = Object.create(null);
    this._onMouseDownListener && this._onMouseDownListener.remove();
    this._onMouseUpListener && this._onMouseUpListener.remove();
    this._onMouseMoveListener && this._onMouseMoveListener.remove();
    this._onKeyUpListener && this._onKeyUpListener.remove();
    this._onKeyDownListener && this._onKeyDownListener.remove();
  }

  isSelected(node) {
    const box = this._selectRect;

    if (!box || !this.selecting) return false;

    return objectsCollide(box, getBoundsForNode(node));
  }

  filter(items) {
    const box = this._selectRect;

    // not selecting
    if (!box || !this.selecting) {
      return [];
    }

    return items.filter(this.isSelected, this);
  }

  _mouseDown(e) {
    const node = this.container ? this.container() : null;
    let collides;
    let offsetData;

    // Right clicks
    if (e.which === 3 || e.button === 2 || !isOverContainer(node, e.clientX, e.clientY)) {
      return;
    }

    if (!this.globalMouse && node && !node.contains(e.target)) {
      const { top, left, bottom, right } = normalizeDistance(0);

      offsetData = getBoundsForNode(node);

      collides = objectsCollide({
        top: offsetData.top - top,
        left: offsetData.left - left,
        bottom: offsetData.bottom + bottom,
        right: offsetData.right + right,
      },
      { top: e.pageY, left: e.pageX });

      if (!collides) return;
    }

    this.emit('mousedown', this._mouseDownData = {
      x: e.pageX,
      y: e.pageY,
      clientX: e.clientX,
      clientY: e.clientY,
    });

    e.preventDefault();

    this._onMouseUpListener = addEventListener('mouseup', this._mouseUp);
    this._onMouseMoveListener = addEventListener('mousemove', this._openSelector);
  }

  _mouseUp(e) {
    this._onMouseUpListener && this._onMouseUpListener.remove();
    this._onMouseMoveListener && this._onMouseMoveListener.remove();

    if (!this._mouseDownData) return undefined;

    const inRoot = !this.container || (this.container() && this.container().contains(e.target));
    const bounds = this._selectRect;
    const click = this.isClick(e.pageX, e.pageY);

    this._mouseDownData = null;

    if (click && !inRoot) {
      return this.emit('reset');
    }

    if (click && inRoot) {
      return this.emit('click', { x: e.pageX, y: e.pageY });
    }

    // User drag-clicked in the Selectable area
    if (!click) {
      return this.emit('select', bounds);
    }

    this.selecting = false;
    return undefined;
  }

  _openSelector(e) {
    if (!this._mouseDownData) return;
    
    const { x, y } = this._mouseDownData;
    const w = Math.abs(x - e.pageX);
    const h = Math.abs(y - e.pageY);

    const left = Math.min(e.pageX, x);
    const top = Math.min(e.pageY, y);
    const old = this.selecting;

    this.selecting = true;

    if (!old) {
      this.emit('selectStart', this._mouseDownData);
    }

    if (!this.isClick(e.pageX, e.pageY)) {
      this._selectRect = {
        top,
        left,
        x: e.pageX,
        y: e.pageY,
        right: left + w,
        bottom: top + h,
        width: w,
        height: h
      };
      this.emit('selecting', this._selectRect);
    }
  }

  _keyListener(e) {
    this.ctrl = (e.metaKey || e.ctrlKey);
  }

  isClick(pageX, pageY) {
    if (!this._mouseDownData) return true;
    
    const { x, y } = this._mouseDownData;
    return (
      Math.abs(pageX - x) <= clickTolerance &&
      Math.abs(pageY - y) <= clickTolerance
    );
  }
}

export default Selection;